import './App.css';
import AllRoutes from './componants/AllRoutes/AllRoutes';
import { Box, Alert, AlertIcon, Text, useToast } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import Navbar from './componants/Home/Navbar/Navbar';
import ZoomControls from './components/BubChat/ZoomControls';
import ChatPanel from './components/BubChat/ChatPanel';
import ScrollToTop from './componants/UI/ScrollToTop';
import ValvePauseGate from './componants/ValveSystem/ValvePauseGate';
import ErrorBoundary from './components/ErrorBoundary';
import ErrorHandler from './utils/ErrorHandler';
import DeviceConfig from './utils/DeviceConfig';
import { useEffect, useRef, useCallback } from 'react';
import { loadAppropriateData, initializeAdminSettings } from './Redux/Mode/action';
import { useLocation } from 'react-router-dom';
import { verifyAuthState } from './utils/authHelper';
import { logout } from './Redux/Login/action';
import Footer from './componants/Footer/Footer';
import UpdatesNotification from './components/UI/UpdatesNotification';
import TermsGate from './componants/Legal/TermsGate';
import { getOptimizedHeroImage, getOptimizedThumbnail } from './utils/imageOptimizer';
import PrimerService from './services/PrimerService';
import apiClient from './utils/apiConfig';

function App() {
  const showDebugButtons = useSelector(state => state.Mode?.showDebugButtons) || false;
  const dispatch = useDispatch();

  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isLegalPage = location.pathname === '/terms' || location.pathname === '/privacy';
  // Valve system: /status renders inside the real app chrome (the page's little mock
  // header was only a placeholder for the real Burrow header — owner correction).
  const isStatusPage = location.pathname === '/status';
  const isChromeless = isLandingPage || isLegalPage;
  // DESIGN DECISION: Chat and Feed are immersive full-screen experiences — no Footer.
  // Do NOT add the footer back to these pages. This is intentional.
  const isImmersivePage = location.pathname === '/chat' || location.pathname === '/feed';
  const toast = useToast();
  const dataLoading = useSelector(state => state.Mode?.loading);
  const movies = useSelector(state => state.Mode?.dataSource?.movies || []);
  const splashRemovedRef = useRef(false);
  
  // Preload critical above-fold images and remove splash screen
  const removeSplash = useCallback(() => {
    if (splashRemovedRef.current) return;
    splashRemovedRef.current = true;
    const loadingEl = document.getElementById('app-loading');
    if (loadingEl) {
      loadingEl.style.opacity = '0';
      loadingEl.style.transition = 'opacity 0.5s ease-out';
      setTimeout(() => loadingEl.remove(), 500);
    }
  }, []);
  
  useEffect(() => {
    if (splashRemovedRef.current) return;
    if (dataLoading !== false) return; // Wait for data to finish loading

    const needsData = !isLandingPage && !isLegalPage && !isStatusPage;
    if (needsData && (!movies || movies.length === 0)) {
      // Data page with no content yet — the data load is about to start (or just
      // finished empty). KEEP the splash as the mask: it must never lift into a
      // skeleton page. This effect re-runs when dataLoading/movies change.
      return;
    }
    if (!movies || movies.length === 0) {
      // Non-data page (terms/privacy/landing) — nothing to wait for
      removeSplash();
      return;
    }
    
    // Preload critical above-fold images before removing splash
    const burrowPicks = movies.filter(m => {
      const tags = Array.isArray(m.tags) ? m.tags.map(t => t.toLowerCase()) : [];
      return tags.includes('burrow picks');
    });
    
    // Get first row thumbnails ("test"/Most Popular tag)
    const mostPopular = movies.filter(m => {
      const tags = Array.isArray(m.tags) ? m.tags.map(t => t.toLowerCase()) : [];
      return tags.includes('test');
    });
    
    // Collect URLs to preload: hero picks + first row thumbs  
    const heroUrls = burrowPicks.slice(0, 5).map(v => {
      const thumb = v.thumbnail || v.thumbnailUrl || '';
      const url = thumb.startsWith('http') ? thumb : `https://bunny-burrow-pull-1.b-cdn.net/${thumb}`;
      return getOptimizedHeroImage(url);
    }).filter(Boolean);
    
    const thumbUrls = mostPopular.slice(0, 16).map(v => {
      const thumb = v.thumbnail || v.thumbnailUrl || '';
      const url = thumb.startsWith('http') ? thumb : `https://bunny-burrow-pull-1.b-cdn.net/${thumb}`;
      return getOptimizedThumbnail(url);
    }).filter(Boolean);
    
    const allUrls = [...heroUrls, ...thumbUrls];
    
    if (allUrls.length === 0) {
      removeSplash();
      return;
    }
    
    // Preload images with a timeout safety net. The splash HOLDS (minimum hang)
    // so it masks the skeleton moment: it only lifts after every above-fold
    // thumbnail is in the browser cache AND at least ~1.6s have passed.
    let loaded = 0;
    const total = allUrls.length;
    const splashStart = Date.now();
    let retryTimer = null;
    const MIN_HANG_MS = 1600;
    const tryRemove = () => {
      const elapsed = Date.now() - splashStart;
      if (elapsed >= MIN_HANG_MS) {
        removeSplash();
      } else {
        retryTimer = setTimeout(tryRemove, Math.min(250, MIN_HANG_MS - elapsed));
      }
    };
    const checkDone = () => {
      loaded++;
      if (loaded >= total) tryRemove();
    };
    
    allUrls.forEach(url => {
      const img = new Image();
      img.onload = checkDone;
      img.onerror = checkDone; // Don't block splash on failed images
      img.src = url;
    });
    
    // Safety timeout — remove splash shortly after data is ready even if some
    // thumbnails are slow (cold network); the mask must not overstay.
    const safetyTimeout = setTimeout(() => tryRemove(), 3000);
    
    return () => {
      clearTimeout(safetyTimeout);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [dataLoading, movies, removeSplash, isLandingPage, isLegalPage]);
  
  // Absolute failsafe: never trap the user behind the splash (broken API, etc.)
  useEffect(() => {
    const failsafe = setTimeout(() => removeSplash(), 12000);
    return () => clearTimeout(failsafe);
  }, [removeSplash]);
  
  // Initialize ErrorHandler with Chakra toast and DeviceConfig on mount
  useEffect(() => {
    // Set up error handling with toast notifications
    ErrorHandler.setToast(toast);
    
    // Initialize device detection and apply CSS classes
    DeviceConfig.init();
    
    // Pre-fetch primer video manifest (fire and forget — caches for session)
    PrimerService.init();
    
    // Log device info in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[DeviceConfig]', {
        device: DeviceConfig.device,
        imageTier: DeviceConfig.getImageTier(),
        lowEnd: DeviceConfig.lowEndDevice,
        touch: DeviceConfig.hasTouch
      });
    }
  }, [toast]);
  
  // Load appropriate data when mode changes or on gateway/home pages
  useEffect(() => {
    // Load data for any page that needs it (gateway needs featured videos)
    dispatch(loadAppropriateData());
  }, [dispatch]);
  
  // Add auth validation effect on app start
  const authState = useSelector(state => state.auth);
  useEffect(() => {
    verifyAuthState(authState, dispatch, logout);
  }, [dispatch, authState]);

  // Listen for session-expired events from apiConfig 401 interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('[App] Session expired event received — dispatching logout');
      dispatch(logout());
    };
    window.addEventListener('burrow-session-expired', handleSessionExpired);
    return () => window.removeEventListener('burrow-session-expired', handleSessionExpired);
  }, [dispatch]);

  // Heartbeat — updates lastActive on server every 60s while authenticated
  const token = useSelector(state => state.auth?.token);
  useEffect(() => {
    if (!token) return;
    const sendHeartbeat = () => {
      apiClient.post('/api/user/heartbeat').catch(() => {});
    };
    sendHeartbeat(); // fire immediately on login
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [token]);
  
  useEffect(() => {
    // Initialize admin settings
    dispatch(initializeAdminSettings());
    // Initialize admin inbox settings
    const adminInboxSettings = JSON.parse(localStorage.getItem('adminInboxSettings')) || {
      filters: {
        category: '',
        status: '',
        search: ''
      }
    };
    dispatch({ type: 'ADMIN_INBOX_INITIALIZE', payload: adminInboxSettings });
  }, [dispatch]);
  
  // Add class to html element — always advanced mode now
  useEffect(() => {
    document.documentElement.classList.add('video-playing');
    return () => {
      document.documentElement.classList.remove('video-playing');
    };
  }, []);
  
  return (
    <div className="App" style={{ backgroundColor: isChromeless ? "white" : "#f5f5f7" }}>
      {/* ScrollToTop component - scrolls to top on route change */}
      <ScrollToTop />
      {/* Valve system — catch sectionPaused 503 → member status door */}
      <ValvePauseGate />
      
      {!isChromeless && <Navbar />}
      
      {/* Only show data source indicator on app pages */}
      
      <Box mt={isChromeless ? 0 : "60px"}>
        <ErrorBoundary>
          <TermsGate>
            <AllRoutes />
          </TermsGate>
        </ErrorBoundary>
      </Box>
      
      {/* Footer excluded from immersive pages (chat, feed) by design — do NOT re-add */}
      {!isChromeless && !isImmersivePage && <Footer />}
      
      {/* Conditionally render debug panels based on showDebugButtons */}

      {/* Floating Zoom Controls - always visible for accessibility */}
      <ZoomControls />

      {/* Floating Chat Panel — desktop only, persists across navigation.
          Not on /status: the member door is a calm standalone scene. */}
      {!isStatusPage && <ChatPanel />}
    </div>
  );
}

export default App;
